import React, { useLayoutEffect, useState } from "react";
import { DEFAULT_SELECT_STYLES } from "../common/AdvancedFilterDropdown";
import { DropDownComponent } from "../../dropdown/DropDownComponent";
import { PlaceHolder } from "../../constants/PlaceHolderConstants";
import { getStorageData } from "../../utils/Validate";

const FILTER_FIELDS = [
  {
    value: "project",
    label: "Project",
    dpDownName: "project",
    placeholder: PlaceHolder.SELECT_PROJECT,
  },
  {
    value: "distributor",
    label: "Distributor",
    dpDownName: "distributor",
    placeholder: PlaceHolder.SELECT_DISTRIBUTOR,
  },
  {
    value: "customer",
    label: "Customer",
    dpDownName: "customer",
    placeholder: "Choose a customer",
  },
  {
    value: "country",
    label: "Country",
    dpDownName: "country",
    placeholder: PlaceHolder.SELECT_COUNTRY,
  },
  {
    value: "quoteDate",
    label: "Quote Date",
  },
];

const getDataSource = (field, dataSources) => {
  if (dataSources?.[field]) return dataSources[field];
  if (field === "project") return dataSources.projectData;
  if (field === "distributor") return dataSources.distributorData;
  if (field === "customer") return dataSources.customerData;
  if (field === "country") return dataSources.countryData;
  if (field === "salesEngineer") return dataSources.salesEngineerData;
  return [];
};

const getFieldConfig = (field, availableFields = FILTER_FIELDS) =>
  availableFields.find((filterField) => filterField.value === field) || availableFields[0] || FILTER_FIELDS[0];

const QuotationFilterDropdown = ({
  addFilterRow,
  countryData,
  customerData,
  dataSources,
  distributorData,
  filterFields,
  filterRows,
  handleFilterDateChange,
  handleFilterFieldChange,
  handleFilterValueChange,
  onCloseFilter,
  projectData,
  removeFilterRow,
  salesEngineerData,
  ClearFilter,
  ApplyFilter,
  positioningTargetRef,
}) => {
  const [menuPosition, setMenuPosition] = useState(null);
  const baseFields = filterFields || FILTER_FIELDS;
  const availableFields =
    getStorageData()?.userType === 2
      ? baseFields.filter((field) => field.value !== "distributor")
      : baseFields;
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;
  const closeFilterDropdown = (event) => {
    event.stopPropagation();
    onCloseFilter?.();
  };

  useLayoutEffect(() => {
    if (!positioningTargetRef?.current || typeof window === "undefined") return undefined;

    const updateMenuPosition = () => {
      const targetRect = positioningTargetRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const rightEdge = Math.min(viewportWidth - 12, targetRect.right);
      const maxWidth = Math.min(800, viewportWidth - 24);
      const preferredWidth = Math.min(maxWidth, Math.max(620, targetRect.width));
      const availableWidth = Math.max(320, rightEdge - 12);
      const width = Math.min(preferredWidth, availableWidth);
      const top = Math.min(targetRect.bottom + 8, viewportHeight - 96);

      setMenuPosition({
        "--quote-filter-top": `${Math.max(8, top)}px`,
        "--quote-filter-left": `${Math.max(12, rightEdge - width)}px`,
        "--quote-filter-width": `${width}px`,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [positioningTargetRef, filterRows.length]);

  const filterSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    border: "1px solid #c9d8e6",
    borderRadius: 7,
    backgroundColor: "#ffffff",
    boxShadow: "none",
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    height: 30,
    padding: "0 10px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 30,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "5px 8px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0054b9",
    fontSize: 13,
    fontWeight: 500,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 100000,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 100000,
    border: "1px solid #c9d8e6",
    boxShadow: "0 12px 26px rgba(36, 73, 104, 0.18)",
  }),
  };

  return (
    <div
      className={`dropdown-menu filter-dropdown quote-filter-menu p-0 show ${menuPosition ? "quote-filter-menu-fixed" : ""}`}
      aria-labelledby="filterDropdown"
      onClick={(event) => event.stopPropagation()}
      style={menuPosition || undefined}
    >
      <div className="quote-filter-panel">
        <div className="quote-filter-header">
          <div>
            <h6>
              <i className="fas fa-filter mr-2"></i>
              Filters
            </h6>
            <p>Build focused list conditions</p>
          </div>
          <button
            className="btn filter-close-btn"
            type="button"
            onClick={closeFilterDropdown}
            title="Close filters"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="quote-filter-builder mb-2">
          {filterRows.map((row, index) => {
            const fieldConfig = getFieldConfig(row.field, availableFields);
            const inputId = `quotation-filter-${row.id}`;

            const usedFieldsInOtherRows = filterRows
              .filter((otherRow) => otherRow.id !== row.id)
              .map((otherRow) => otherRow.field);
            return (
              <div
                className={`quote-filter-row ${row.field === "quoteDate" ? "quote-filter-row-date" : ""}`}
                key={row.id}
              >
                <div className="filter-field-cell">
                  <label htmlFor={`${inputId}-field`}>Filter by</label>
                  <select
                    id={`${inputId}-field`}
                    name={`${inputId}-field`}
                    className="form-control"
                    value={row.field}
                    onChange={(event) => handleFilterFieldChange(row.id, event.target.value)}
                  >
                    {availableFields.map((field) => (
                      <option
                        key={field.value}
                        value={field.value}
                        disabled={usedFieldsInOtherRows.includes(field.value)}
                      >
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                {row.field === "quoteDate" ? (
                  <div className="filter-date-range-cell">
                    <div className="filter-date-range-label">Date range</div>
                    <div className="quote-date-range-control">
                      <div className="date-range-segment">
                        <span>From</span>
                        <input
                          id={`${inputId}-from`}
                          name={`${inputId}-from`}
                          type="date"
                          className="form-control"
                          aria-label="Quote date from"
                          value={row.fromDate}
                          onChange={(event) =>
                            handleFilterDateChange(row.id, "fromDate", event.target.value)
                          }
                        />
                      </div>
                      <div className="date-range-divider"></div>
                      <div className="date-range-segment">
                        <span>To</span>
                        <input
                          id={`${inputId}-to`}
                          name={`${inputId}-to`}
                          type="date"
                          className="form-control"
                          aria-label="Quote date to"
                          value={row.toDate}
                          onChange={(event) =>
                            handleFilterDateChange(row.id, "toDate", event.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="filter-value-cell">
                    <label htmlFor={`${inputId}-value`}>{fieldConfig.label}</label>
                    <DropDownComponent
                      inputId={`${inputId}-value`}
                      placeHolder={fieldConfig.placeholder}
                      pageName="quotation"
                      dpDownName={fieldConfig.dpDownName}
                      stateData={getDataSource(row.field, {
                        countryData,
                        customerData,
                        ...(dataSources || {}),
                        distributorData,
                        projectData,
                        salesEngineerData,
                      })}
                      controlName={`${row.field}-${index}`}
                      handleChange={(event) => handleFilterValueChange(row.id, event)}
                      value={row.value}
                      setState={() => { }}
                      multiSelect={false}
                      menuPortalTarget={menuPortalTarget}
                      customStyles={DEFAULT_SELECT_STYLES}
                    />
                  </div>
                )}

                {filterRows.length > 1 && (
                  <button
                    className="btn filter-row-delete"
                    type="button"
                    onClick={() => removeFilterRow(row.id)}
                    title="Remove filter"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="quote-filter-footer">
          <button
            className="btn add-filter-row-btn"
            type="button"
            onClick={addFilterRow}
            disabled={filterRows.length >= availableFields.length}
          >
            <i className="fas fa-plus mr-2"></i>
            Add Filter
          </button>
          <div className="filter-action-group">
            <button
              className="btn reset-btn"
              type="button"
              onClick={(event) => {
                ClearFilter();
                closeFilterDropdown(event);
              }}
            >
              Reset
            </button>
            <button
              className="btn apply-btn"
              type="button"
              onClick={() => {
                ApplyFilter();
                onCloseFilter?.();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(QuotationFilterDropdown);
